import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { DirectionsBoat } from '@mui/icons-material';

import { FerryLeg, FerryOffer, ferryLegs, isRoundTrip } from '../ferry';
import { formatLocalTime } from '../rail';
import { useAudience } from '../audience';
import { MetaLine } from './MetaLine';
import { PriceBlock } from './PriceBlock';
import { ScheduleTimes } from './ScheduleTimes';
import { StatusFlag } from './StatusFlag';
import { RuteroResultCard } from './RuteroResultCard';

interface Props {
  offer: FerryOffer;
  /** Modelo elegido en la búsqueda: la tarifa lo incluye si viaja embarcado. */
  vehicleName?: string;
  bookingDisabled?: boolean;
  onBook: (offer: FerryOffer) => void;
  /** Posición en la lista de resultados: da el escalonado de la entrada. */
  index?: number;
}

/**
 * Una travesía cotizada, en el arquetipo de SALIDAS: las horas de puerto son
 * el título y la naviera es una firma en la línea de identidad — su logo ya no
 * ocupa una caja de media que no vendía nada.
 *
 * En ida y vuelta el proveedor devuelve los DOS tramos en una sola oferta con
 * el precio TOTAL: se muestran ambos bloques de horarios y la columna aclara
 * qué cubre el importe.
 */
export function FerryOfferCard({
  offer,
  vehicleName,
  bookingDisabled,
  onBook,
  index,
}: Props) {
  const { t } = useTranslation();
  const { isAgent } = useAudience();
  const [logoFailed, setLogoFailed] = useState(false);

  const roundTrip = isRoundTrip(offer);
  const legs = ferryLegs(offer);
  const operatorLogoUrl = logoFailed ? undefined : offer.operatorLogoUrl;
  /**
   * La naviera. Cuando el proveedor no la publica, el respaldo es el conector
   * — pero ese nombre es información de OPERACIÓN y no puede caer en una
   * superficie minorista solo porque falte el dato de arriba.
   */
  const operatorName = offer.operator ?? (isAgent ? offer.provider : undefined);

  /**
   * Lo que hay que saber del trayecto en sí. El aviso del operador cuelga como
   * tooltip para no gastar altura de card con su texto completo.
   */
  const crossingNote = () => {
    const parts = [
      offer.busTransferIncluded ? t('travelServices.ferry.busIncluded') : '',
      typeof offer.checkInMinutes === 'number'
        ? t('travelServices.ferry.checkIn', { count: offer.checkInMinutes })
        : '',
    ].filter(Boolean);
    if (parts.length === 0) return undefined;

    const text = parts.join(' · ');
    return offer.providerMessages?.length ? (
      <Tooltip title={offer.providerMessages.join(' ')}>
        <Box component="span" sx={{ cursor: 'help' }}>
          {text}
        </Box>
      </Tooltip>
    ) : (
      text
    );
  };

  /**
   * El rótulo del tramo: el día siempre, y en ida y vuelta precedido por qué
   * tramo es. Los vacíos se descartan para no dejar un separador colgando
   * cuando el proveedor no publicó la fecha.
   */
  const legCaption = (leg: FerryLeg) =>
    [
      roundTrip
        ? t(
          leg.isReturn
            ? 'travelServices.booking.return'
            : 'travelServices.booking.outbound',
        )
        : '',
      leg.date,
    ]
      .filter(Boolean)
      .join(' · ');

  /** Hechos de la travesía: texto quieto con separadores, no una sopa de chips. */
  const facts = [
    roundTrip ? t('travelServices.ferry.roundTrip') : '',
    offer.vesselName ?? '',
    vehicleName ?? '',
  ].filter(Boolean);

  return (
    <RuteroResultCard
      index={index}
      header={
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {operatorLogoUrl ? (
            <Box
              component="img"
              src={operatorLogoUrl}
              alt={operatorName ?? ''}
              loading="lazy"
              sx={{ height: 18, maxWidth: 72, objectFit: 'contain' }}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <DirectionsBoat sx={{ fontSize: 18 }} color="disabled" />
          )}
          {operatorName && (
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
              {operatorName}
            </Typography>
          )}
          {/* Por dónde se compra es información de operación: en una
              superficie minorista no se muestra. */}
          {isAgent && offer.operator && offer.provider !== offer.operator && (
            <Typography component="span" color="text.disabled" sx={{ fontSize: 11.5 }}>
              {t('travelServices.viaProvider', { provider: offer.provider })}
            </Typography>
          )}
          {/* El dato existe en la oferta desde siempre y no se veía hasta el
              modal de reserva: el operador RECHAZA el booking sin plaza. */}
          {offer.accommodationCompulsory && (
            <Box ml="auto">
              <StatusFlag
                tone="info"
                label={t('travelServices.ferry.assignedSeatRequired')}
              />
            </Box>
          )}
        </Stack>
      }
      title={
        <Stack spacing={1}>
          {legs.map((leg) => (
            <Box key={leg.isReturn ? 'return' : 'outbound'}>
              {/* La fecha va SIEMPRE, con ida y vuelta o sin ella: un horario
                  sin día no es un itinerario, y con resultados retenidos en
                  pantalla es lo único que distingue una salida de otra. */}
              {legCaption(leg) && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {legCaption(leg)}
                </Typography>
              )}
              <ScheduleTimes
                departure={{
                  time: formatLocalTime(leg.departure),
                  place: leg.fromPort ?? '—',
                }}
                arrival={{
                  time: formatLocalTime(leg.arrival),
                  place: leg.toPort ?? '—',
                }}
                duration={leg.duration ?? t('travelServices.ferry.crossing')}
                note={leg.isReturn ? undefined : crossingNote()}
              />
            </Box>
          ))}
        </Stack>
      }
      details={
        <MetaLine
          items={[
            ...facts,
            // La letra chica de la tarifa vive junto a los hechos del viaje y
            // no enterrada en "condiciones": la flexibilidad decide.
            offer.ticketConditions ? (
              <Box
                component="span"
                key="conditions"
                sx={{ color: 'warning.dark', fontWeight: 600 }}
              >
                {offer.ticketConditions}
              </Box>
            ) : undefined,
          ]}
        />
      }
      price={
        <PriceBlock
          pricing={offer.pricing}
          unit={
            roundTrip
              ? t('travelServices.ferry.totalRoundTrip')
              : t('travelServices.ferry.totalOneWay')
          }
        />
      }
      action={
        <Button
          variant="contained"
          sx={{ minWidth: 104, height: 30 }}
          disabled={bookingDisabled}
          onClick={() => onBook(offer)}
        >
          {t('travelServices.book')}
        </Button>
      }
    />
  );
}
